import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Image,
  InputGroup,
  Modal,
  Row,
} from "react-bootstrap";
import { BsFillSendFill, BsGlobe, BsTrash } from "react-icons/bs";
import { FaEdit } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { useDeletePost } from "../../api/mutations/useDeletePost";
import { useAppContext } from "../../Context/AppContext";
import { Post } from "../../types/Types";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";

interface PostCardProps {
  post: { Media?: string } & Post;
  refetchPosts: () => void;
  setEditPostData: (post: Post | null) => void;
}

const TRUNCATED_TEXT_STYLE: React.CSSProperties = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const FONT_SIZE_14_STYLE: React.CSSProperties = { fontSize: "14px" };
const FONT_SIZE_13_STYLE: React.CSSProperties = { fontSize: "13px" };
const COMMENT_INPUT_STYLE: React.CSSProperties = {
  boxShadow: "none",
  outline: "none",
};

const MEDIA_IMAGE_STYLE: React.CSSProperties = {
  objectFit: "contain",
  maxHeight: "400px",
};
function TruncateText({
  html,
  lines = 4,
}: {
  html: string;
  lines?: number;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const el = contentRef.current;
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
  }, [html]);

  const handleExpand = useCallback(() => {
    setExpanded(true);
  }, []);

  const handleCollapse = useCallback(() => {
    setExpanded(false);
  }, []);

  const clampStyle = useMemo(
    () => ({
      ...TRUNCATED_TEXT_STYLE,
      WebkitLineClamp: lines,
    }),
    [lines]
  );

  return (
    <div>
      <div
        ref={contentRef}
        style={expanded ? {} : clampStyle}
        className="mb-3"
      >
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
      {isClamped && !expanded && (
        <span
          className="text-dark fw-semibold bg-transparent small"
          role="button"
          tabIndex={0}
          onClick={handleExpand}
          onKeyDown={(e) => e.key === "Enter" && handleExpand()}
          aria-label="See more"
        >
          {t("feed.seeMore")}
        </span>
      )}
      {expanded && (
        <span
          className="text-dark fw-semibold bg-transparent small"
          role="button"
          tabIndex={0}
          onClick={handleCollapse}
          onKeyDown={(e) => e.key === "Enter" && handleCollapse()}
          aria-label="See less"
        >
          {t("feed.seeLess")}
        </span>
      )}
    </div>
  );
}

TruncateText.displayName = "TruncateText";

const MemoizedTruncateText = memo(TruncateText);

function PostCard({ post, refetchPosts, setEditPostData }: PostCardProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAppContext();
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");
  const [showRemovePostModal, setShowRemovePostModal] = useState(false);
  const [comments, setComments] = useState<string[]>([
    "Tanim Zaman 🥳",
    "Love this",
  ]);

  const onDeleteSuccess = useCallback(() => {
    setShowRemovePostModal(false);
    refetchPosts();
  }, [refetchPosts]);

  const { mutate: removePost, isPending: isDeleting } = useDeletePost({
    onSuccess: onDeleteSuccess,
  });

  const handleCommentSubmit = useCallback(() => {
    if (comment.trim()) {
      setComments((prev) => [...prev, comment.trim()]);
      setComment("");
    }
  }, [comment]);

  const handleRemovePost = useCallback(() => {
    if (post.id !== undefined) {
      removePost(post.id);
    }
  }, [post.id, removePost]);

  const handleEditPost = useCallback(() => {
    setEditPostData(post);
  }, [post, setEditPostData]);

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value);
  }, []);

  const handleCommentKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleCommentSubmit();
    }
  }, [handleCommentSubmit]);

  const handleCloseRemoveModal = useCallback(() => {
    setShowRemovePostModal(false);
  }, []);

  const handleOpenRemoveModal = useCallback(() => {
    setShowRemovePostModal(true);
  }, []);

  const handleToggleCommentBox = useCallback(() => {
    setShowCommentBox((prev: boolean) => !prev);
  }, []);

  const formattedDate = useMemo(() => {
    const postDate = new Date(post.createdDate + "Z");
    const localTime = postDate.toLocaleString(i18n.resolvedLanguage || i18n.language || "en", {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const now = new Date();
    const isToday = postDate.toDateString() === now.toDateString();
    return isToday
      ? localTime
      : postDate.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  }, [i18n.language, i18n.resolvedLanguage, post.createdDate]);
  const { data: translatedPost } = useTranslateDynamicFields(
    {
      content: post.content || "",
      title: post.title || "",
      articleContent: post.articleContent || "",
    },
    i18n.resolvedLanguage || i18n.language
  );

  return (
    <>
      <Card className="shadow-sm border rounded mb-3 position-relative z-index-0">
        {user?.user.isAdmin === 1 && (
          <div className="dropdown position-absolute top-0 end-0 m-2 z-1">
            <button
              type="button"
              className="border-0 rounded-1 px-2 bg-transparent text-dark"
              id="postActionsDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-label="Post actions"
            >
              <IoIosArrowDown />
            </button>
            <ul
              className="dropdown-menu dropdown-menu-end"
              aria-labelledby="postActionsDropdown"
            >
              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={handleEditPost}
                >
                  <FaEdit size={16} className="me-2" /> {t("feed.editPost")}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="dropdown-item text-danger"
                  onClick={handleOpenRemoveModal}
                >
                  <BsTrash size={16} className="me-2" /> {t("feed.removePost")}
                </button>
              </li>
            </ul>
          </div>
        )}
        <Card.Body className="pt-2 pb-0">
          <Row className="align-items-start">
            <Col xs="auto">
              <Image
                src="/logoOld.png"
                roundedCircle
                width={48}
                height={48}
                alt="User"
                loading="lazy"
                decoding="async"
              />
            </Col>
            <Col>
              <div className="fw-bold">{t("feed.brandName")}</div>
              <div className="text-muted" style={FONT_SIZE_14_STYLE}>
                {t("feed.brandSubtitle")}
              </div>
              <div
                className="text-muted d-flex align-items-center"
                style={FONT_SIZE_13_STYLE}
              >
                {formattedDate}{" "}
                <BsGlobe className="ms-2" />
              </div>
            </Col>
          </Row>
          <div className="mt-3">
            <MemoizedTruncateText html={translatedPost?.content || post.content} lines={4} />
            {!post.isArticle && post.Media && (
              <Image
                src={post.Media}
                alt="Post media"
                className="img-fluid rounded bg-dark w-100 border mt-2"
                style={MEDIA_IMAGE_STYLE}
              />
            )}
          </div>
          {post.isArticle === 1 && (
            <div className="m-2 m-md-4 border rounded p-1 p-md-3 bg-light">
              {post.Media && (
                <Image
                  src={post.Media}
                  alt="Cover"
                  fluid
                  className="rounded mb-3"
                  width={800}
                  height={267}
                  loading="lazy"
                  decoding="async"
                />
              )}
              {post.title && (
                <h1 className="mb-3">{translatedPost?.title || post.title || t("feed.untitled")}</h1>
              )}
              <MemoizedTruncateText
                html={translatedPost?.articleContent || post.articleContent || `<p><i>${t("feed.noContent")}</i></p>`}
                lines={6}
              />
            </div>
          )}
        </Card.Body>

        <Card.Footer className="bg-white pt-2 px-3 border-0">
          <div className="d-flex justify-content-start mb-2">
            <Button
              type="button"
              variant="link"
              className="text-muted p-0 d-flex align-items-center gap-1"
              onClick={handleToggleCommentBox}
            >
              <BsFillSendFill size={16} />
              <span className="small">{t("feed.comment")}</span>
            </Button>
          </div>
          {/* LinkedIn-style Comment Section */}
          {showCommentBox && (
            <div className="mt-3">
              {/* Comment Input */}
              <div className="d-flex align-items-center">
                <Image
                  src="/user.png"
                  width={36}
                  height={36}
                  roundedCircle
                  className="me-2"
                  alt="Commenter"
                  loading="lazy"
                  decoding="async"
                />
                <InputGroup className="rounded-pill border d-flex align-items-center px-3 py-0 bg-light">
                  <Form.Control
                    placeholder={t("feed.addComment")}
                    value={comment}
                    onChange={handleCommentChange}
                    style={COMMENT_INPUT_STYLE}
                    className="border-0 bg-light"
                    onKeyDown={handleCommentKeyDown}
                    aria-label={t("feed.addComment")}
                  />
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 text-primary"
                      onClick={handleCommentSubmit}
                    >
                      <BsFillSendFill size={18} />
                    </Button>
                  </div>
                </InputGroup>
              </div>
              <hr />

              {/* Display Comments */}
              <div className="mt-2">
                {comments.map((c, i) => (
                  <div key={i} className="d-flex align-items-start gap-2 mb-3">
                    <Image
                      src="/user.png"
                      width={36}
                      height={36}
                      roundedCircle
                      alt="comment"
                    />
                    <div>
                      <div className="fw-semibold small mb-1">
                        {i === 0 ? t("feed.brandName") : t("feed.sampleUser")}{" "}
                        {i === 0 && (
                          <span className="text-muted small ms-1">• 13h</span>
                        )}
                        {i === 1 && (
                          <span className="text-muted small ms-1">• 9h</span>
                        )}
                      </div>
                      <div className="small">{c}</div>
                      <div className="text-muted small mt-1 d-flex gap-3">
                        <span className="cursor-pointer">{t("feed.like")}</span>
                        <span className="cursor-pointer">{t("feed.reply")}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More */}
              <div className="text-center mt-2">
                <Button
                  type="button"
                  variant="link"
                  className="text-muted small"
                >
                  {t("feed.loadMoreComments")}
                </Button>
              </div>
            </div>
          )}
        </Card.Footer>
      </Card>
      <Modal
        centered
        show={showRemovePostModal}
        onHide={handleCloseRemoveModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("feed.removePost")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("feed.removePostConfirm")}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleCloseRemoveModal}
          >
            {t("common.close")}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleRemovePost}
            disabled={isDeleting}
          >
            {isDeleting ? t("feed.removing") : t("feed.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

PostCard.displayName = "PostCard";

export default memo(PostCard);
